import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import { useComments, notifyCommentFailed } from '../hooks/useComments';
import { User, Reply, Trash2, CheckCircle, Play, ArrowUp } from 'lucide-react';
import { apiClient } from '../lib/apiClient';
import { Comment } from '../types/comment';
import type { ViewerProfile } from './SharedVideoPage';
import { toast } from '../lib/toast';
import RichCommentEditor, { sanitizeCommentHtml } from './RichCommentEditor';
import BodyPortal from './BodyPortal';
import { formatClock } from '../lib/editorTime';
import { useReadOnly } from '../hooks/useArchiveAccess';

// ─── Custom confirm dialog (replaces window.confirm) ────────
const ConfirmDeleteModal: React.FC<{
  onConfirm: () => void;
  onCancel: () => void;
}> = ({ onConfirm, onCancel }) => {
  const { t } = useTranslation();
  return (
    <BodyPortal>
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        background: 'rgba(0,0,0,0.45)',
      }}
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          background: 'var(--card, #fff)',
          color: 'var(--cardForeground, #111827)',
          borderRadius: 16,
          padding: '28px 32px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 20,
          boxShadow: '0 8px 40px rgba(0,0,0,0.3)',
          minWidth: 280,
          maxWidth: 360,
          textAlign: 'center',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <Trash2 size={36} style={{ color: 'var(--error, #ef4444)' }} aria-hidden="true" />
        <div>
          <p style={{ fontWeight: 600, fontSize: 16, margin: '0 0 6px' }}>
            {t('comments.delete_confirm')}
          </p>
          <p style={{ fontSize: 13, color: 'var(--textSecondary, #6b7280)', margin: 0 }}>
            {t('comments.delete_confirm_sub', 'This action cannot be undone.')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, width: '100%' }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              flex: 1,
              padding: '10px 0',
              borderRadius: 8,
              border: '1px solid var(--border, #e5e7eb)',
              background: 'var(--cardHoverBg, #f3f4f6)',
              color: 'var(--foreground, #111827)',
              fontWeight: 500,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {t('cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            style={{
              flex: 1,
              padding: '10px 0',
              borderRadius: 8,
              border: 'none',
              background: 'var(--error, #ef4444)',
              color: '#fff',
              fontWeight: 600,
              fontSize: 14,
              cursor: 'pointer',
            }}
          >
            {t('comments.delete_button', 'Delete')}
          </button>
        </div>
      </div>
    </div>
    </BodyPortal>
  );
};

// ─── Confirmation modal shown after successful delete ────────
const DeletedModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation();

  useEffect(() => {
    const timer = setTimeout(onClose, 2500);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <BodyPortal>
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        background: 'rgba(0,0,0,0.35)',
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          background: 'var(--card, #fff)',
          color: 'var(--cardForeground, #111827)',
          borderRadius: 16,
          padding: '32px 40px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 16,
          boxShadow: '0 8px 40px rgba(0,0,0,0.25)',
          minWidth: 260,
          textAlign: 'center',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <CheckCircle
          size={48}
          style={{ color: 'var(--attendancePresentFg, #16a34a)' }}
          aria-hidden="true"
        />
        <p style={{ fontWeight: 600, fontSize: 16, margin: 0 }}>
          {t('comments.deleted_confirmation', 'Comment removed')}
        </p>
        <p style={{ fontSize: 13, color: 'var(--textSecondary, #6b7280)', margin: 0 }}>
          {t('comments.deleted_confirmation_sub', 'The comment has been deleted successfully.')}
        </p>
      </div>
    </div>
    </BodyPortal>
  );
};

interface CommentsProps {
  videoId: string;
  onCommentAdded?: () => void;
  onCommentDeleted?: () => void;
  className?: string;
  /** When true, the current user is the video owner and can delete any comment */
  isOwner?: boolean;
  // ── Shared-video mode (Approaches A + B) ──────────────────────────────────
  /** Resolved viewer identity (null = not yet identified) */
  viewerProfile?: ViewerProfile | null;
  /** Called when the viewer successfully identifies via the inline email form */
  /** ownerSlug of the video — used to look up the viewer in OrganizationUsers */
  videoSlug?: string;
  /** The page's player: new comments can be attached to its current time, and times seek it. */
  playerRef?: React.RefObject<HTMLVideoElement | null>;
}

interface CommentItemProps {
  comment: Comment;
  replies: Comment[];
  allComments: Comment[];
  isOwner: boolean;
  /** False for viewers who can't comment (anonymous): no Reply button. */
  canReply: boolean;
  /** Signed-in user / identified viewer; authors are matched by email. */
  currentUserEmail?: string | null;
  videoId: string;
  onDelete: (commentId: string) => void;
  onReply: (parentId: string, content: string) => Promise<void>;
  /** Jumps the player to a comment's time (absent when there is no player). */
  onSeek?: (seconds: number) => void;
  depth?: number;
}

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'Just now';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Recently';
    return new Intl.DateTimeFormat(navigator.language, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch {
    return 'Recently';
  }
};

const createdMs = (c: Comment) => Date.parse(c.created_at || (c as any).createdAt || '') || 0;
/** Replies read like a conversation: oldest first (top-level comments stay newest first). */
const inThreadOrder = (list: Comment[]) => [...list].sort((a, b) => createdMs(a) - createdMs(b));

/**
 * "Back to top" for a long thread: shown once whatever holds the comments (the comments
 * column, the player window or the page) has been scrolled down a long way; it stays at the
 * bottom of the view while the comments are on screen.
 */
const BackToTop: React.FC = () => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [scroller, setScroller] = useState<HTMLElement | null>(null);
  useEffect(() => {
    // Scroll events don't bubble: listen in the capture phase and keep the one that holds us.
    const onScroll = (e: Event) => {
      const el = e.target instanceof HTMLElement ? e.target : document.scrollingElement as HTMLElement | null;
      if (!el || !ref.current || !el.contains(ref.current)) return;
      setScroller(el.scrollTop > 600 ? el : null);
    };
    document.addEventListener('scroll', onScroll, true);
    return () => document.removeEventListener('scroll', onScroll, true);
  }, []);
  return (
    <div ref={ref} style={{ position: 'sticky', bottom: 12, display: 'flex', justifyContent: 'center', pointerEvents: 'none', height: 0, overflow: 'visible' }}>
      {scroller && (
        <button
          type="button"
          onClick={() => scroller.scrollTo({ top: 0, behavior: 'smooth' })}
          className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-xs font-semibold"
          style={{
            pointerEvents: 'auto', transform: 'translateY(-100%)',
            background: 'var(--theme-primary, #4f46e5)', color: 'var(--button-primary-foreground, #fff)',
            boxShadow: '0 4px 14px rgba(0,0,0,0.22)', border: 'none', cursor: 'pointer',
          }}
        >
          <ArrowUp className="w-3.5 h-3.5" aria-hidden="true" />
          {t('comments.back_to_top', 'Back to top')}
        </button>
      )}
    </div>
  );
};

const CommentItem: React.FC<CommentItemProps> = ({
  comment,
  replies,
  allComments,
  isOwner,
  canReply,
  currentUserEmail,
  videoId,
  onDelete,
  onReply,
  onSeek,
  depth = 0,
}) => {
  const { t } = useTranslation();
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [submittingReply, setSubmittingReply] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const readOnly = useReadOnly();

  // comment.userId is a backend user UUID the app doesn't have, so match the author by email.
  // (Comparing ids let `undefined === undefined` show Delete on everyone's comments.)
  const commentAuthorEmail = ((comment as any).authorEmail || comment.user?.email || comment.user_email || '')
    .toString().toLowerCase();
  const isAuthor = !!currentUserEmail && !!commentAuthorEmail && commentAuthorEmail === currentUserEmail.toLowerCase();
  // Not while the school is read-only (Admin Center Archive Mode): the server refuses it.
  const canDelete = (isOwner || isAuthor) && !readOnly;
  const isPlaceholder = (v: unknown) =>
    !v || v === 'Unknown User' || v === 'Visitante';

  const authorName =
    (!isPlaceholder((comment as any).authorName)  ? (comment as any).authorName  : null) ||
    (!isPlaceholder(comment.user_profile?.full_name) ? comment.user_profile?.full_name : null) ||
    (!isPlaceholder((comment as any).authorEmail) ? (comment as any).authorEmail  : null) ||
    (!isPlaceholder(comment.user?.email)          ? comment.user?.email           : null) ||
    (!isPlaceholder(comment.user_email)           ? comment.user_email            : null) ||
    t('comments.anonymous_user');

  const handleReplySubmit = async (html: string) => {
    setSubmittingReply(true);
    try {
      await onReply(comment.id, html);
      setShowReplyForm(false);
    } finally {
      setSubmittingReply(false);
    }
  };

  const handleDelete = () => setShowConfirm(true);
  const handleConfirmedDelete = () => {
    setShowConfirm(false);
    onDelete(comment.id);
  };

  return (
    <div style={{ marginLeft: depth > 0 ? 32 : 0 }}>
      {showConfirm && (
        <ConfirmDeleteModal
          onConfirm={handleConfirmedDelete}
          onCancel={() => setShowConfirm(false)}
        />
      )}
      <div className="flex gap-3">
        {/* Avatar */}
        <div className="flex-shrink-0">
          {comment.user_profile?.avatar_url ? (
            <img
              src={comment.user_profile.avatar_url}
              alt={authorName}
              className="w-9 h-9 rounded-full object-cover"
            />
          ) : (
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center"
              style={{ background: 'var(--primary, #6366f1)', color: 'var(--button-primary-foreground, #fff)' }}
            >
              <User className="w-4 h-4" aria-hidden="true" />
            </div>
          )}
        </div>

        {/* Bubble */}
        <div className="flex-1 min-w-0">
          <div
            className="rounded-xl px-4 py-3"
            style={{ background: 'var(--cardHoverBg, #f3f4f6)', color: 'var(--cardForeground, #111827)' }}
          >
            <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5">
              <span className="flex items-center gap-2 min-w-0">
                <span className="font-semibold text-sm truncate">{authorName}</span>
                {typeof comment.timeSeconds === 'number' && (
                  <button
                    type="button"
                    onClick={() => onSeek?.(comment.timeSeconds!)}
                    disabled={!onSeek}
                    title={onSeek ? t('comments.time_play', 'Play from {{time}}', { time: formatClock(comment.timeSeconds) }) : undefined}
                    className="flex-shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
                    style={{
                      background: 'var(--surfaceSelected, #ede9fe)',
                      color: 'var(--surfaceSelectedForeground, #4f46e5)',
                      fontVariantNumeric: 'tabular-nums',
                      cursor: onSeek ? 'pointer' : 'default',
                    }}
                  >
                    <Play className="w-3 h-3" fill="currentColor" aria-hidden="true" />
                    {formatClock(comment.timeSeconds)}
                  </button>
                )}
              </span>
              <span
                className="text-xs flex-shrink-0"
                style={{ color: 'var(--textSecondary, #6b7280)' }}
              >
                {formatDate(comment.created_at || comment.createdAt)}
              </span>
            </div>
            {/* Rich HTML content (TipTap) or plain text (legacy) */}
            {comment.content?.trim().startsWith('<') ? (
              <div
                className="comment-rich-content mt-1"
                style={{ color: 'var(--foreground, #111827)' }}
                dangerouslySetInnerHTML={{ __html: sanitizeCommentHtml(comment.content) }}
              />
            ) : (
              <p className="mt-1 text-sm" style={{ color: 'var(--foreground, #111827)' }}>
                {comment.content}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 mt-1 px-1">
            {depth === 0 && canReply && (
              <button
                type="button"
                onClick={() => setShowReplyForm((v) => !v)}
                className="flex items-center gap-1 text-xs"
                style={{ color: 'var(--textSecondary, #6b7280)' }}
              >
                <Reply className="w-3.5 h-3.5" aria-hidden="true" />
                {t('comments.reply_button', 'Reply')}
              </button>
            )}
            {canDelete && (
              <button
                type="button"
                onClick={handleDelete}
                className="flex items-center gap-1 text-xs"
                style={{
                  color: 'var(--error, #ef4444)',
                }}
                aria-label={t('comments.delete_button')}
              >
                <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                {isOwner && !isAuthor
                  ? t('comments.moderate_button', 'Remove')
                  : t('comments.delete_button')}
              </button>
            )}
          </div>

          {/* Inline reply form — uses the same RichCommentEditor as top-level comments */}
          {showReplyForm && (
            <div className="mt-2 space-y-1">
              <RichCommentEditor
                videoId={videoId}
                placeholder={t('comments.reply_placeholder', 'Write a reply…')}
                loading={submittingReply}
                onSubmit={handleReplySubmit}
              />
              <button
                type="button"
                onClick={() => setShowReplyForm(false)}
                className="text-xs px-2 py-1 rounded"
                style={{ color: 'var(--textSecondary, #6b7280)' }}
              >
                {t('cancel', 'Cancel')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Nested replies */}
      {replies.length > 0 && (
        <div className="mt-2 space-y-2 border-l-2 pl-3" style={{ borderColor: 'var(--border, #e5e7eb)', marginLeft: 18 }}>
          {replies.map((reply) => (
            <CommentItem
              key={reply.id}
              comment={reply}
              replies={inThreadOrder(allComments.filter((c) => (c.parentId || c.parent_id) === reply.id))}
              allComments={allComments}
              isOwner={isOwner}
              canReply={canReply}
              currentUserEmail={currentUserEmail}
              videoId={videoId}
              onDelete={onDelete}
              onReply={onReply}
              onSeek={onSeek}
              depth={depth + 1}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Comments: React.FC<CommentsProps> = ({
  videoId,
  className = '',
  onCommentAdded,
  onCommentDeleted,
  isOwner = false,
  viewerProfile,
  videoSlug,
  playerRef,
}) => {
  const { t } = useTranslation();
  const [, setNewComment] = useState(''); // kept for legacy; content managed by RichCommentEditor
  const [showDeletedModal, setShowDeletedModal] = useState(false);

  const { user } = useAuth();
  const { comments, loading, error, addComment, refetch, removeLocal } = useComments(videoId);

  // True when running in shared-video context (viewer profile props were provided)
  const isSharedMode = videoSlug !== undefined;
  // The effective commenter: authenticated user (normal app) OR identified viewer (shared page)
  const effectiveUser = user ?? (viewerProfile ? { email: viewerProfile.email, id: '' } : null);
  // Same rule as the comment box below: in the app a signed-in user, on the share page an ACV sign-in.
  // Not while the school is read-only (Admin Center Archive Mode).
  const readOnly = useReadOnly();
  const canComment = !readOnly && (isSharedMode ? !!viewerProfile : !!user);


  // Owner (moderation) or author. Errors are shown as toasts, and the comment leaves the
  // list right away instead of waiting for a refetch.
  const handleDelete = async (commentId: string) => {
    const { error: deleteError } = isOwner
      ? await apiClient.deleteCommentAsOwner(commentId)
      : await apiClient.deleteComment(commentId);
    if (deleteError) {
      console.error('Error deleting comment:', deleteError);
      toast.error(
        /not authorized/i.test(deleteError.message)
          ? t('comments.delete_not_allowed', 'You can only delete your own comments.')
          : t('comments.delete_failed', 'Could not delete the comment. Please try again.'),
      );
      return;
    }
    removeLocal(commentId);
    onCommentDeleted?.();
    setShowDeletedModal(true);
  };

  // Comment times: bring the player into view, jump there and play.
  const handleSeek = playerRef ? (seconds: number) => {
    const video = playerRef.current;
    if (!video) return;
    video.currentTime = seconds;
    video.scrollIntoView({ behavior: 'smooth', block: 'center' });
    void video.play().catch(() => { /* autoplay refused: stays at the time, paused */ });
  } : undefined;

  const handleReply = async (parentId: string, content: string) => {
    const { data, error } = await apiClient.addComment(videoId, content, parentId);
    // Throwing keeps the reply text (and the form open) after a failure.
    if (error || !data) {
      notifyCommentFailed(error);
      throw error ?? new Error('Reply not posted');
    }
    refetch();
    onCommentAdded?.();
  };

  // Split into top-level and replies
  const topLevel = comments.filter((c) => !c.parentId && !c.parent_id);
  const repliesFor = (parentId: string) =>
    inThreadOrder(comments.filter((c) => c.parentId === parentId || c.parent_id === parentId));

  return (
    <div className={`space-y-4 ${className}`}>
      {showDeletedModal && <DeletedModal onClose={() => setShowDeletedModal(false)} />}

      {/* ── Viewer identity chip (shared-video mode, already identified) ── */}
      {isSharedMode && viewerProfile && (
        <div
          className="flex items-center gap-2 text-sm rounded-lg px-3 py-2"
          style={{
            background: viewerProfile.isIdentified
              ? 'var(--attendancePresentBg, #f0fdf4)'
              : 'var(--surfaceSelected, #ede9fe)',
            color: viewerProfile.isIdentified
              ? 'var(--attendancePresentFg, #16a34a)'
              : 'var(--surfaceSelectedForeground, #4f46e5)',
          }}
        >
          <User className="w-4 h-4 flex-shrink-0" aria-hidden="true" />
          <span>
            {viewerProfile.isIdentified
              ? `${viewerProfile.name} · ${viewerProfile.email}`
              : viewerProfile.email}
          </span>
        </div>
      )}

      {/* ── Rich text comment editor ─────────────────────────────────────── */}
      {readOnly ? (
        <p className="text-sm px-1" style={{ color: 'var(--textSecondary, #6b7280)' }}>
          {t('archive.comments_read_only', "Comments are turned off while your school's account is read-only.")}
        </p>
      ) : !canComment && !isSharedMode ? (
        /* In the app without a signed-in user (e.g. a public channel opened outside ACV):
           comments are read-only instead of failing on submit. */
        <p className="text-sm px-1" style={{ color: 'var(--textSecondary, #6b7280)' }}>
          {t('comments.sign_in_prompt')}
        </p>
      ) : !canComment ? (
        /* Share page outside ACV: commenting needs a verified ACV sign-in. */
        <p className="text-sm px-1" style={{ color: 'var(--textSecondary, #6b7280)' }}>
          {t('comments.sign_in_acv', 'Sign in with ACV to comment.')}
        </p>
      ) : (
        <RichCommentEditor
          videoId={videoId}
          placeholder={t('comments.add_placeholder')}
          disabled={loading}
          loading={loading}
          getTime={playerRef ? () => playerRef.current?.currentTime ?? null : undefined}
          onSubmit={async (html, timeSeconds) => {
            setNewComment(html); // keep state in sync for legacy path
            // addComment already showed the error; throwing keeps the text and time in the box.
            if (!(await addComment(html, timeSeconds))) throw new Error('Comment not posted');
            setNewComment('');
            onCommentAdded?.();
          }}
        />
      )}

      {error && (
        <div
          className="p-3 text-sm rounded-lg"
          style={{ background: 'var(--attendanceAbsentBg, #fef2f2)', color: 'var(--attendanceAbsentFg, #ef4444)' }}
        >
          {error}
        </div>
      )}

      {/* Moderation banner */}
      {isOwner && comments.length > 0 && (
        <div
          className="px-3 py-2 rounded-lg text-xs flex items-center gap-2"
          style={{ background: 'var(--surfaceSelected, #ede9fe)', color: 'var(--surfaceSelectedForeground, #4f46e5)' }}
        >
          <Trash2 className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
          {t('comments.owner_mode_banner', 'You own this video, so you can remove any comment.')}
        </div>
      )}

      {/* Comments list */}
      <div className="space-y-4 mt-2">
        {loading && comments.length === 0 ? (
          <div className="flex justify-center py-8">
            <div
              className="animate-spin rounded-full h-8 w-8 border-b-2"
              style={{ borderColor: 'var(--primary, #6366f1)' }}
            />
          </div>
        ) : topLevel.length === 0 ? (
          <div className="text-center py-8 text-sm" style={{ color: 'var(--textSecondary, #6b7280)' }}>
            {t('comments.no_comments')}
          </div>
        ) : (
          <div className="space-y-4">
            {topLevel.map((comment) => (
              <CommentItem
                key={comment.id}
                comment={comment}
                replies={repliesFor(comment.id)}
                allComments={comments}
                isOwner={isOwner}
                canReply={canComment}
                currentUserEmail={effectiveUser?.email}
                videoId={videoId}
                onDelete={handleDelete}
                onReply={handleReply}
                onSeek={handleSeek}
              />
            ))}
          </div>
        )}
      </div>
      <BackToTop />
    </div>
  );
};

export default Comments;
